import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  FetchBlockItemDimensions,
  getAppliedFilters,
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  getSelectedBlockDimension,
  handleFilters,
  resetSelectedBlockDimension,
  setFilterItems,
  setMultiDimFilter,
  setSelectedBlockDimensionDirect,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch, getTableFilterPayload } from 'utils/GlobalHelpers';

import {
  buildDefaultDraft,
  countActiveFilterItems,
  selectionsFromReduxForDisplay,
  selectionsToRedux,
} from '../planDimSelectionUtils';
import {
  PLAN_FILTER_BADGE_BG,
  PLAN_FILTER_PILL_HEIGHT,
  PLAN_TEXT,
  PLAN_TOOLBAR_ICON_SIZE,
  neutral,
  text as textToken,
} from '../planPageRevampUi';
import { PlanToolbarChevronDownIcon, PlanToolbarFilterIcon } from '../icons';

import PlanDimSelectionModal, { type PlanDimensionOption } from './PlanDimSelectionModal';

import type { PlanDimDraftSelections } from '../planDimSelectionUtils';

interface Properties {
  onApplyFilters?: () => void;
}

const PlanFilterByControl = ({ onApplyFilters }: Properties) => {
  const { blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const blockDimensions = useSelector(getBlockDimensions);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const appliedFilters = useSelector(getAppliedFilters);
  const blockDetails = useSelector(getBlockDetails);
  const indicatorTableData = useSelector(getIndicatorTableData);
  const baseScenario = useSelector(getBaseScenario);
  const themeColors = useSelector(getPlanPageThemeColors);

  const [isOpen, setIsOpen] = useState(false);
  const [activeDimensionName, setActiveDimensionName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftSelections, setDraftSelections] = useState<PlanDimDraftSelections>({});

  const dimensions = useMemo<PlanDimensionOption[]>(
    () =>
      (blockDimensions?.dimensions ?? []).map((dimension: any) => ({
        id: dimension.id,
        name: dimension.name,
        items: dimension.items ?? [],
      })),
    [blockDimensions?.dimensions],
  );

  const visibleDimensions = useMemo(
    () => dimensions.filter((dimension) => dimension.items.length > 0),
    [dimensions],
  );

  const activeFilterSource = useMemo(
    () => (appliedFilters && appliedFilters.length > 0 ? appliedFilters : selectedBlockDimension),
    [appliedFilters, selectedBlockDimension],
  );

  // A dimension with every one of its items selected isn't filtering
  // anything — "Clear all" puts selections back in that full state, so
  // counting raw selected items (countActiveFilterItems) kept showing the
  // pre-clear badge count forever. Only a selection narrower than the full
  // item list represents an active filter.
  const dimensionTotalItemCounts = useMemo(
    () => new Map(dimensions.map((dimension) => [dimension.name, dimension.items.length])),
    [dimensions],
  );

  const narrowedFilterEntries = useMemo(
    () =>
      (activeFilterSource ?? []).filter((entry: any) => {
        const dimensionName = Object.keys(entry ?? {})[0];
        const values = dimensionName ? entry[dimensionName] : null;
        if (!Array.isArray(values)) return false;
        const totalItems = dimensionTotalItemCounts.get(dimensionName) ?? 0;
        return values.length > 0 && values.length < totalItems;
      }),
    [activeFilterSource, dimensionTotalItemCounts],
  );

  const activeFilterCount = useMemo(
    () => countActiveFilterItems(narrowedFilterEntries),
    [narrowedFilterEntries],
  );

  // Which dimensions actually have a narrowed selection, not just which are
  // listed — `selectedBlockDimension`/`appliedFilters` carries an entry per
  // dimension regardless of whether it has any items checked.
  const activeFilterDimensionNames = useMemo(
    () => narrowedFilterEntries.map((entry: any) => Object.keys(entry ?? {})[0]).filter(Boolean),
    [narrowedFilterEntries],
  );

  // With exactly one dimension filtered, naming it beats a bare count — the
  // user can see at a glance what's actually being filtered on without
  // opening the modal.
  const filterLabel =
    activeFilterDimensionNames.length === 1 ? activeFilterDimensionNames[0] : 'Filter by';

  useEffect(() => {
    dispatch(
      FetchBlockItemDimensions({
        blockId,
        parameters: {
          params: { scenario_id: baseScenario?.id },
        },
      }),
    );
  }, [baseScenario?.id, blockId, dispatch]);

  useEffect(() => {
    if (blockDetails?.model_id && blockDetails?.id && blockDimensions?.dimensions?.length > 0) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data = JSON.parse(planFilters);
          const modalFilters = data[blockDetails.model_id]?.[blockDetails.id];
          if (modalFilters?.selectedBlockDimension) {
            dispatch(setSelectedBlockDimensionDirect(modalFilters.selectedBlockDimension));
          }
        }
      } catch {
        // Ignore invalid localStorage payload
      }
    }
  }, [blockDetails?.id, blockDetails?.model_id, blockDimensions?.dimensions, dispatch]);

  const persistFilters = useCallback(
    (selections: any[]) => {
      if (!blockDetails?.model_id || !blockDetails?.id) return;
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data: Record<string, any> = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modelData: Record<string, any> = data[modelId] || {};
        const blockFilters: any = modelData[currentBlockId] || {};
        blockFilters.selectedBlockDimension = selections;
        modelData[currentBlockId] = blockFilters;
        data[modelId] = modelData;
        localStorage.setItem('global-modal-filters', JSON.stringify(data));
      } catch {
        // Ignore localStorage write failures
      }
    },
    [blockDetails?.id, blockDetails?.model_id],
  );

  const applyIndicatorTableFilters = useCallback(
    (selectionsOverride?: any[]) => {
      const selections = selectionsOverride ?? selectedBlockDimension;
      const activeDimIds = indicatorTableData?.dimensions?.map((item: any) => item.id);
      selections.forEach((selection: any, index: number) => {
        const selectedValues = Object.values(selection)[0] as string[];
        const dimensionMeta = blockDimensions?.dimensions?.[index];
        if (
          selectedValues?.length > 0 &&
          dimensionMeta &&
          activeDimIds?.includes(String(dimensionMeta.id))
        ) {
          const payload = getTableFilterPayload(selection, dimensionMeta);
          dispatch(setFilterItems(payload.dimensionItems));
          dispatch(setMultiDimFilter(payload.tableItems));
        }
      });
    },
    [blockDimensions?.dimensions, dispatch, indicatorTableData?.dimensions, selectedBlockDimension],
  );

  const openModal = useCallback(() => {
    const draft = selectionsFromReduxForDisplay(selectedBlockDimension, visibleDimensions);
    const fallbackDimension = visibleDimensions[0]?.name ?? '';
    setDraftSelections(
      Object.keys(draft).length > 0 ? draft : buildDefaultDraft(visibleDimensions),
    );
    setActiveDimensionName(fallbackDimension);
    setSearchQuery('');
    setIsOpen(true);
  }, [selectedBlockDimension, visibleDimensions]);

  const handleApply = useCallback(() => {
    // A dimension left untouched shows unchecked in the modal (the "no
    // filter" default), but Redux's established convention for "no filter"
    // on a dimension is every item selected, not none — expand it back here
    // so downstream filter logic (badge, hasSubsetFilter, table query) keeps
    // working exactly as it already does for the unfiltered case.
    const expandedDraft: PlanDimDraftSelections = {};
    visibleDimensions.forEach((dimension) => {
      const selected = draftSelections[dimension.name];
      expandedDraft[dimension.name] = selected && selected.length > 0 ? selected : dimension.items;
    });
    const payload = selectionsToRedux(expandedDraft);
    dispatch(setSelectedBlockDimensionDirect(payload));
    dispatch(handleFilters());
    persistFilters(payload);
    // applyIndicatorTableFilters defaults to the `selectedBlockDimension`
    // closure, which is synchronous dispatch but won't reflect in this
    // render until next render — pass the freshly built payload directly.
    applyIndicatorTableFilters(payload);
    setIsOpen(false);
    onApplyFilters?.();
  }, [
    applyIndicatorTableFilters,
    dispatch,
    draftSelections,
    onApplyFilters,
    persistFilters,
    visibleDimensions,
  ]);

  const handleClearAll = useCallback(async () => {
    // resetSelectedBlockDimension is a synchronous reducer — selectedBlockDimension
    // in this closure won't reflect the reset until next render, so the "select
    // everything" payload is rebuilt here to drive applyIndicatorTableFilters now.
    const resetSelections = (blockDimensions?.dimensions ?? []).map(({ name, items }: any) => ({
      [name]: items,
    }));
    await dispatch(resetSelectedBlockDimension());
    const resetDraft = buildDefaultDraft(visibleDimensions);
    setDraftSelections(resetDraft);
    applyIndicatorTableFilters(resetSelections);

    if (blockDetails?.model_id && blockDetails?.id) {
      try {
        const planFilters = localStorage.getItem('global-modal-filters');
        if (planFilters) {
          const data: Record<string, any> = JSON.parse(planFilters);
          const modelId = String(blockDetails.model_id);
          const currentBlockId = String(blockDetails.id);
          const modelData: Record<string, any> = data[modelId] || {};
          if (modelData[currentBlockId]) {
            delete modelData[currentBlockId].selectedBlockDimension;
            data[modelId] = modelData;
            localStorage.setItem('global-modal-filters', JSON.stringify(data));
          }
        }
      } catch {
        // Ignore localStorage clear failures
      }
    }

    setIsOpen(false);
    onApplyFilters?.();
  }, [
    applyIndicatorTableFilters,
    blockDetails?.id,
    blockDetails?.model_id,
    blockDimensions?.dimensions,
    dispatch,
    onApplyFilters,
    visibleDimensions,
  ]);

  const handleToggleItem = useCallback(
    (dimensionName: string, itemName: string, checked: boolean) => {
      setDraftSelections((previous) => {
        const currentItems = new Set(previous[dimensionName] ?? []);
        if (checked) {
          currentItems.add(itemName);
        } else {
          currentItems.delete(itemName);
        }
        return {
          ...previous,
          [dimensionName]: Array.from(currentItems),
        };
      });
    },
    [],
  );

  return (
    <>
      <Button
        size='sm'
        h={PLAN_FILTER_PILL_HEIGHT}
        px={0}
        py={0}
        w='fit-content'
        {...PLAN_TEXT.toolbarTab}
        border='none'
        borderRadius='0'
        bg='transparent'
        color={textToken.primary}
        display='inline-flex'
        alignItems='center'
        flexWrap='nowrap'
        gap={2}
        lineHeight='1'
        boxShadow='none'
        _hover={{ bg: neutral.surfaceHover }}
        _focus={{ boxShadow: 'none' }}
        onClick={openModal}
      >
        <PlanToolbarFilterIcon
          width={PLAN_TOOLBAR_ICON_SIZE}
          height={PLAN_TOOLBAR_ICON_SIZE}
          color={textToken.secondary}
        />
        <Text {...PLAN_TEXT.toolbarTab} whiteSpace='nowrap' lineHeight='1.2'>
          {filterLabel}
        </Text>
        {activeFilterCount > 0 && (
          <Flex
            minW='18px'
            h='18px'
            px={1}
            borderRadius='full'
            bg={PLAN_FILTER_BADGE_BG}
            color={themeColors.white}
            {...PLAN_TEXT.badge}
            alignItems='center'
            justifyContent='center'
            flexShrink={0}
          >
            {activeFilterCount}
          </Flex>
        )}
        <PlanToolbarChevronDownIcon width={10} height={6} color={textToken.muted} />
      </Button>

      <PlanDimSelectionModal
        isOpen={isOpen}
        dimensions={dimensions}
        activeDimensionName={activeDimensionName}
        draftSelections={draftSelections}
        searchQuery={searchQuery}
        onClose={() => setIsOpen(false)}
        onApply={handleApply}
        onClearAll={handleClearAll}
        onActiveDimensionChange={setActiveDimensionName}
        onSearchChange={setSearchQuery}
        onToggleItem={handleToggleItem}
      />
    </>
  );
};

export default memo(PlanFilterByControl);
